import { Key, Plus, ShieldCheck, Smartphone, Trash2, User2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createUser, deleteUser, listUsers, resetUserPassword, setUserPhones, updateUser,
  type AdminUser, type CreateUserPayload,
} from "../api/users";
import { listPhones, type Phone } from "../api/phones";
import type { UserRole } from "../api/auth";
import { useAuth } from "../hooks/useAuth";
import { StatusBadge } from "../components/StatusBadge";
import { formatDateTime, relativeTime } from "../utils/format";

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [phones, setPhones] = useState<Phone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [phonesModalUser, setPhonesModalUser] = useState<AdminUser | null>(null);
  const [resetPwUser, setResetPwUser] = useState<AdminUser | null>(null);

  const reload = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([listUsers(), listPhones()]);
      setUsers(u);
      setPhones(p);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function toggleActive(u: AdminUser) {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("common.error"));
    }
  }

  async function changeRole(u: AdminUser, role: UserRole) {
    if (u.role === role) return;
    try {
      await updateUser(u.id, { role });
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("common.error"));
    }
  }

  async function removeUser(u: AdminUser) {
    if (!window.confirm(t("users.confirmDelete", { email: u.email }))) return;
    try {
      await deleteUser(u.id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("common.error"));
    }
  }

  if (loading) return <div className="p-8 text-ink-500">{t("common.loading")}</div>;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">{t("users.title")}</h1>
          <p className="text-sm text-ink-500 mt-1">
            {t(users.length > 1 ? "users.countSuffix_plural" : "users.countSuffix", { n: users.length })}
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium transition-colors"
        >
          <Plus className="size-4" />
          {t("users.addBtn")}
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-ink-700 bg-ink-800/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-700/40 text-ink-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{t("users.columns.user")}</th>
              <th className="text-left px-4 py-3">{t("users.columns.role")}</th>
              <th className="text-left px-4 py-3">{t("users.columns.phones")}</th>
              <th className="text-left px-4 py-3">{t("users.columns.status")}</th>
              <th className="text-left px-4 py-3">{t("users.columns.lastLogin")}</th>
              <th className="text-right px-4 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/50">
            {users.map((u) => {
              const isSelf = !!(currentUser && u.id === currentUser.id);
              return (
                <tr key={u.id} className="hover:bg-ink-700/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.role === "admin" ? (
                        <ShieldCheck className="size-4 text-velvet-300" />
                      ) : (
                        <User2 className="size-4 text-ink-500" />
                      )}
                      <div>
                        <div className="text-white font-medium">{u.name}</div>
                        <div className="text-xs text-ink-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={u.role}
                      onChange={(e) => void changeRole(u, e.target.value as UserRole)}
                      disabled={isSelf}
                      className="bg-ink-700 border border-ink-600 rounded-md px-2 py-1 text-xs text-white disabled:opacity-50"
                      title={isSelf ? t("users.selfRoleLocked") : undefined}
                    >
                      <option value="admin">{t("users.role.admin")}</option>
                      <option value="employee">{t("users.role.employee")}</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <span className="text-xs text-ink-500">{t("users.fullAccess")}</span>
                    ) : u.assigned_phones.length === 0 ? (
                      <span className="text-xs text-amber-400">{t("users.none")}</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {u.assigned_phones.map((p) => (
                          <span
                            key={p.id}
                            className="text-xs px-2 py-0.5 rounded-full bg-ink-700 text-ink-300 border border-ink-600"
                          >
                            {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => void toggleActive(u)}
                      disabled={isSelf}
                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                      title={isSelf ? t("users.selfDeactivateLocked") : undefined}
                    >
                      <StatusBadge status={u.is_active ? "active" : "deleted"} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-ink-400 text-xs">
                    {u.last_login_at ? (
                      <span title={formatDateTime(u.last_login_at)}>
                        {relativeTime(u.last_login_at)}
                      </span>
                    ) : (
                      <span className="text-ink-600">{t("common.never")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {u.role === "employee" && (
                        <button
                          onClick={() => setPhonesModalUser(u)}
                          className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-velvet-300 transition-colors"
                          title={t("users.columns.phones")}
                        >
                          <Smartphone className="size-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setResetPwUser(u)}
                        className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-amber-300 transition-colors"
                        title={t("users.resetPassword.submit")}
                      >
                        <Key className="size-4" />
                      </button>
                      <button
                        onClick={() => void removeUser(u)}
                        disabled={isSelf}
                        className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isSelf ? t("users.selfDeleteLocked") : t("common.delete")}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateUserModal
          phones={phones}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await reload();
          }}
        />
      )}

      {phonesModalUser && (
        <AssignPhonesModal
          user={phonesModalUser}
          phones={phones}
          onClose={() => setPhonesModalUser(null)}
          onSaved={async () => {
            setPhonesModalUser(null);
            await reload();
          }}
        />
      )}

      {resetPwUser && (
        <ResetPasswordModal
          user={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onDone={() => setResetPwUser(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({
  phones, onClose, onCreated,
}: {
  phones: Phone[];
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}) {
  const [form, setForm] = useState<CreateUserPayload>({
    email: "", name: "", password: "", role: "employee", phone_ids: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createUser({
        ...form,
        phone_ids: form.role === "employee" ? form.phone_ids : [],
      });
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  function togglePhone(id: string) {
    setForm((f) => {
      const cur = f.phone_ids ?? [];
      return cur.includes(id)
        ? { ...f, phone_ids: cur.filter((x) => x !== id) }
        : { ...f, phone_ids: [...cur, id] };
    });
  }

  return (
    <ModalShell title="Ajouter un utilisateur" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <input
            type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-ink-700 border border-ink-600 rounded-md px-3 py-2 text-sm text-white"
          />
        </Field>
        <Field label="Nom">
          <input
            type="text" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-ink-700 border border-ink-600 rounded-md px-3 py-2 text-sm text-white"
          />
        </Field>
        <Field label="Mot de passe initial (8 caractères min)">
          <input
            type="text" required minLength={8} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-ink-700 border border-ink-600 rounded-md px-3 py-2 text-sm text-white font-mono"
            placeholder="L'employé pourra le changer après"
          />
        </Field>
        <Field label="Rôle">
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
            className="w-full bg-ink-700 border border-ink-600 rounded-md px-3 py-2 text-sm text-white"
          >
            <option value="employee">Employé (accès limité aux tels assignés)</option>
            <option value="admin">Admin (accès complet)</option>
          </select>
        </Field>

        {form.role === "employee" && (
          <Field label="Téléphones assignés">
            <div className="border border-ink-600 rounded-md max-h-48 overflow-auto">
              {phones.length === 0 ? (
                <div className="px-3 py-3 text-xs text-ink-500">Aucun téléphone enrôlé</div>
              ) : (
                phones.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-ink-700/40 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={(form.phone_ids ?? []).includes(p.id)}
                      onChange={() => togglePhone(p.id)}
                    />
                    <span className="text-white">{p.name}</span>
                    <StatusBadge status={p.computed_status ?? p.status} />
                  </label>
                ))
              )}
            </div>
          </Field>
        )}

        {err && <div className="text-sm text-red-300">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-ink-400 hover:bg-ink-700/50">
            Annuler
          </button>
          <button
            type="submit" disabled={busy}
            className="px-3 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Création…" : "Créer"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function AssignPhonesModal({
  user, phones, onClose, onSaved,
}: {
  user: AdminUser;
  phones: Phone[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [selected, setSelected] = useState<string[]>(user.assigned_phones.map((p) => p.id));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await setUserPhones(user.id, selected);
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Téléphones de ${user.name}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-ink-500">
          Coche les téléphones auxquels {user.email} a accès. La liste s'applique
          immédiatement (cache TTL ~60s).
        </p>
        <div className="border border-ink-600 rounded-md max-h-72 overflow-auto">
          {phones.length === 0 ? (
            <div className="px-3 py-3 text-xs text-ink-500">Aucun téléphone enrôlé</div>
          ) : (
            phones.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-ink-700/40 cursor-pointer text-sm"
              >
                <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                <span className="text-white flex-1">{p.name}</span>
                <StatusBadge status={p.computed_status ?? p.status} />
              </label>
            ))
          )}
        </div>
        {err && <div className="text-sm text-red-300">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-ink-400 hover:bg-ink-700/50">
            Annuler
          </button>
          <button
            onClick={() => void save()} disabled={busy}
            className="px-3 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Enregistrement…" : `Enregistrer (${selected.length})`}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function ResetPasswordModal({
  user, onClose, onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pw.length < 8) { setErr("Minimum 8 caractères"); return; }
    setBusy(true);
    setErr(null);
    try {
      await resetUserPassword(user.id, pw);
      setDone(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Reset password — ${user.email}`} onClose={onClose}>
      {done ? (
        <div className="space-y-4">
          <div className="text-sm text-emerald-300">
            ✓ Mot de passe réinitialisé. Communique-le à {user.name} :
          </div>
          <code className="block px-3 py-2 bg-ink-900 border border-ink-600 rounded-md text-velvet-300 break-all">
            {pw}
          </code>
          <div className="flex justify-end">
            <button onClick={onDone} className="px-3 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium">
              OK
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nouveau mot de passe (8 caractères min)">
            <input
              type="text" required minLength={8} value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full bg-ink-700 border border-ink-600 rounded-md px-3 py-2 text-sm text-white font-mono"
              autoFocus
            />
          </Field>
          {err && <div className="text-sm text-red-300">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-ink-400 hover:bg-ink-700/50">
              Annuler
            </button>
            <button
              type="submit" disabled={busy}
              className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-50"
            >
              {busy ? "Reset…" : "Reset"}
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-ink-800 border border-ink-700 rounded-xl p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-1 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors">
            <X className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-ink-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
